<template>
	<div v-click-outside="onClickOutside" class="help-wrapper" :class="{ 'as-icon': !hasSlot }" :data-label="label">

		<div class="help-wrapper__slot" :class="{'help-not-ready': !isHelpAvailable}" @click="openHelp">
			<template v-if="hasSlot">
				<slot />
			</template>
			<template v-else>
				<fa v-if="isHelpAvailable" class="help-wrapper__slot__icon" :icon="['fas', 'question-circle']" />
			</template>
		</div>

		<teleport to="body">
			<div
				v-if="active"
				:key="label"
				ref="tooltip"
				class="help-wrapper__tooltip"
				:class="{ 'as-icon': !hasSlot }"
				:style="{ top: `${tooltip_top}px`, left: `${tooltip_left}px` }"
			>

				<header
					v-if="title && title !== ''"
					class="help-wrapper__tooltip__header"
					:class="[`left-offset-${tooltip_left_offset || 15}`]"
				>
					<h6 v-text="title"></h6>
					<span class="help-wrapper__tooltip__header__close" @click="active = false">
						<fa :icon="['fas', 'times']" />
					</span>
				</header>

				<div
					class="help-wrapper__tooltip__content"
					v-html="content || '<p>No help content to show.</p>'"
				/>
			</div>
		</teleport>
	</div>
</template>

<script lang="ts">
	import { GeneralHelper } from '../helper/general';
	import { HttpHelper } from '../helper/http';
	import { Options, Vue } from 'vue-class-component';

	@Options({
		name: 'Help',
		props: ['label'],
		inject: ['http', 'helper'],
	})
	export default class Help extends Vue {
		public http!: HttpHelper;
		public helper!: GeneralHelper;
		public label!: string;
		public previous_label!: string;
		public title = 'Loading...';
		public content = '<p>Please Wait</p>';
		public active = false;
		public has_loaded = false;
		public left_offset = 0;
		public tooltip_left_offset = 15;
		public tooltip_top = 0;
		public tooltip_left = 0;
		public is_in_modal = false;
		public tooltip_width = 350;

		public get hasSlot(): boolean {
			if (!this.$slots) return false;
			return Object.keys(this.$slots).length > 0;
		}

		public get hasContent(): boolean {
			if (this.title && this.title !== '' && this.content && this.content !== '') return true;
			return false;
		}

		public get isHelpAvailable(): boolean {
			if (this.label && this.label !== '' && this.label !== 'undefined') {
				if (this.$help && this.$help[this.label]) return true;
			}
			return false;
		}

		public async openHelp(): Promise<void> {
			if (!this.isHelpAvailable) return;

			await this.requestHelp();
			this.active = !this.active;
			this.$nextTick(() => {
				this.calculateOffset();
			});
		}

		public onClickOutside(): void {
			if (this.active) this.active = false;
		}

		public async requestHelp(): Promise<void> {
			if (this.has_loaded && this.previous_label === this.label) return;
			this.has_loaded = true;
			this.previous_label = this.label;
			const response = await this.http.get(`/api/help/${this.label}`);
			if (response.status === 200) {
				const result = await response.json();
				this.title = result.title || 'No help found';
				this.content = result.content || '';
			}
		}

		public calculateOffset(): void {
			const slotElement = this.$el.querySelector('.help-wrapper__slot') as HTMLElement;
			if (!slotElement) return;

			const rect = slotElement.getBoundingClientRect();

			this.tooltip_top = rect.top + slotElement.offsetHeight + 15;
			this.tooltip_left = rect.left + 10;
		}

		public mounted(): void {
			const el = this.$el as HTMLElement;
			this.is_in_modal = el.closest('.modal-inner-container') !== null;

			const update = () => {
				if (this.active) this.calculateOffset();
			};

			window.addEventListener('resize', update);
			window.addEventListener('scroll', update, true);
		}
	}
</script>

<style lang="scss">
	@import '../assets/styles/base';

	.list-column .help-wrapper__slot {
		font-weight: 600;
	}

	.help-not-ready {
		cursor: unset;
		text-decoration: none;
	}

	.help-wrapper__tooltip__content {
		max-height: 300px;
		overflow-y: scroll;
		padding-right: 1rem;
		scrollbar-color: rgba(50, 72, 103, 0) transparent;
		scrollbar-gutter: stable;
		font-family: $primary-font-family;
	}

	.help-wrapper__tooltip__content:hover {
		scrollbar-color: rgb(50, 72, 103) transparent;
	}
	.help-wrapper__tooltip.fixed-tooltip {
		position: fixed;
		z-index: 9999;
	}
</style>
